import GenderRevealPricingTable from "@/components/GenderRevealPricingTable";
import PeekabooGallery from "@/components/PeekabooGallery";
import SetmoreBooking from "@/components/SetmoreBooking";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Baby, Clock, Shield, Star } from "lucide-react";
import SEO from "@/components/SEO";

const GenderReveal = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50">
      <SEO
        title="Gender Reveal Testing - Peekaboo by DDC | Early & Accurate Results"
        description="Discover your baby's gender as early as 6 weeks with 99.9% accuracy. Rush results available in 1-2 days. Mobile testing service comes to you."
        keywords="gender reveal test, peekaboo test, baby gender, early gender test, DDC gender test, mobile testing"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "MedicalTest",
          "name": "Gender Reveal Testing - Peekaboo by DDC",
          "description": "Early and accurate gender determination testing with mobile service",
          "provider": {
            "@type": "MedicalBusiness",
            "name": "Near Lab"
          }
        }}
      />

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Baby className="h-8 w-8 text-pink-600" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-pink-600 to-blue-600 bg-clip-text text-transparent">
              Gender Reveal Testing
            </h1>
            <Baby className="h-8 w-8 text-blue-600" />
          </div>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-6">
            Discover your baby's gender with confidence using the trusted Peekaboo test by DDC. 
            Early results, exceptional accuracy, and mobile convenience.
          </p>
          <Badge variant="secondary" className="bg-gradient-to-r from-pink-100 to-blue-100 text-pink-800 text-lg px-6 py-2">
            99.9% Accuracy • As Early as 6 Weeks
          </Badge>
        </div>

        {/* Peekaboo Gallery */}
        <div className="mb-12">
          <PeekabooGallery />
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="border-pink-200 hover:shadow-lg transition-shadow">
            <CardHeader className="text-center">
              <Clock className="h-8 w-8 text-pink-600 mx-auto mb-2" />
              <CardTitle className="text-lg text-pink-800">Early Detection</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-center">
                Get results as early as 6 weeks into pregnancy with our advanced testing technology.
              </p>
            </CardContent>
          </Card>

          <Card className="border-blue-200 hover:shadow-lg transition-shadow">
            <CardHeader className="text-center">
              <Shield className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <CardTitle className="text-lg text-blue-800">99.9% Accurate</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-center">
                Trust in DDC's proven accuracy with over 2 million tests performed worldwide.
              </p>
            </CardContent>
          </Card>

          <Card className="border-purple-200 hover:shadow-lg transition-shadow">
            <CardHeader className="text-center">
              <Star className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <CardTitle className="text-lg text-purple-800">Mobile Service</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-center">
                Convenient at-home or office testing with our certified mobile technicians.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Table */}
        <div className="mb-12">
          <GenderRevealPricingTable />
        </div>

        {/* Booking Section */}
        <div className="mb-12">
          <SetmoreBooking />
        </div>

        {/* How It Works */}
        <Card className="mb-12">
          <CardHeader className="bg-gradient-to-r from-pink-50 to-blue-50 border-b">
            <CardTitle className="text-2xl text-center">How It Works</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-pink-600 font-bold text-lg">1</span>
                </div>
                <h3 className="font-semibold mb-2">Schedule Your Test</h3>
                <p className="text-gray-600">Book your appointment and we'll come to you at your convenience.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-blue-600 font-bold text-lg">2</span>
                </div>
                <h3 className="font-semibold mb-2">Simple Blood Draw</h3>
                <p className="text-gray-600">Quick and painless blood sample collection by certified technicians.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-purple-600 font-bold text-lg">3</span>
                </div>
                <h3 className="font-semibold mb-2">Get Your Results</h3>
                <p className="text-gray-600">Receive secure, confidential results within your chosen timeframe.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About DDC */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-pink-50 to-blue-50 border-b">
            <CardTitle className="text-2xl text-center flex items-center justify-center gap-2">
              <Baby className="h-6 w-6 text-pink-600" />
              About Peekaboo by DDC
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="max-w-4xl mx-auto">
              <p className="text-gray-700 text-lg mb-6 text-center">
                DDC (DNA Diagnostic Centre) is the world's most trusted DNA testing company, 
                having performed over 2 million paternity and other DNA tests.
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-pink-800 mb-3">What Makes Peekaboo Special:</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                      Non-invasive blood test - safe for mom and baby
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                      Works as early as 6 weeks gestation
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                      Available for singleton and twin pregnancies
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                      Rush results available in 1-2 business days
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-800 mb-3">Why Choose DDC:</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      Industry leader with 25+ years experience
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      AABB accredited laboratory
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      Confidential and secure results
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      Outstanding customer support
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GenderReveal;